<template>
  <main>
    <v-row class="form-box">
      <h4 v-if="loadingData">Loading ...</h4>

      <template v-else>
        <v-col sm="12" class="pb-0">
          <article>
            <h4 class="mb-5" v-if="phase.name">
              <strong>{{ phase.order + " Of " + phase.total }}</strong
              ><span class="gray-color ml-5">{{ phase.name }}</span>
            </h4>
            <p v-if="phase.intro" v-html="phase.intro" class="mb-p-0"></p>
          </article>
        </v-col>

        <v-col sm="12" md="8">
          <v-row>
            <v-col v-if="globalServerErrors !== null" cols="12">
              <v-alert dense outlined type="error">
                <p
                  class="mb-0"
                  v-for="(globalErr, errIndex) in globalServerErrors"
                  :key="errIndex"
                  v-html="globalErr[0]"
                ></p>
              </v-alert>
            </v-col>

            <v-col
              :sm="question.width || '12'"
              class="py-0"
              cols="12"
              v-for="question in phase.questions"
              :key="question.index"
            >
              <Question :form="form" :question="question" />
            </v-col>
            <v-col cols="12">
              <v-btn
                v-if="phase.next"
                @click="handleSave('next')"
                color="primary"
                large
                class="text-capitalize radius-14 px-10 mt-8"
                ><strong>Next</strong></v-btn
              >
              <v-btn
                @click="handleSave('proceed')"
                v-if="phase.next == null && phase.id"
                large
                color="success"
                class="text-capitalize radius-14 px-10 mt-9 ml-2"
                >Proceed</v-btn
              >
              <v-btn
                v-if="phase.previous"
                @click="handleSave('prev')"
                text
                large
                outlined
                class="text-capitalize radius-14 px-10 mt-9 ml-2"
                >Previous</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </template>
    </v-row>
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      ><Proceed @closeDialog="closeDialog"
    /></v-dialog>
  </main>
</template>

<script>
import { IndexData, ShowData, UpdateData } from "@/helpers/apiMethods";

export default {
  data() {
    return {
      application: {},
      phase: {},
      form: {},
      loadingData: false,
      dialog: false,
      globalServerErrors: null
    };
  },
  components: {
    Question: () => import("./components/Question"),
    Proceed: () => import("./components/Proceed")
  },
  created() {
    this.indexApplication();
  },
  methods: {
    setFormData(questions) {
      questions.forEach(ques => {
        const quesData = this.getQuestionData(ques);
        if (quesData || quesData === 0) {
          this.form[ques.id] = quesData;
        }
      });
    },
    getQuestionData(question) {
      if (question.choices && question.choices.length) {
        // handle questions embedded within questions choices
        question.choices.forEach(choice => {
          if (choice.questions && choice.questions.length) {
            this.setFormData(choice.questions);
          }
        });
      }

      if (
        question.answer &&
        typeof question.answer === "object" &&
        question.answer !== null
      ) {
        if (question.bag != null) {
          return this.getBagQuestionData(question);
        }
        if (!question.answer.path) {
          if (question.answer.questions) {
            this.setFormData(question.answer.questions);
          }
          return question.answer.id;
        }
      } else if (question.answer || question.answer === 0) {
        return question.answer;
      }
      return null;
    },
    getBagQuestionData(question) {
      const bagQuesData = {
        id: question.id,
        answer: [...question.answer],
        bagData: {
          selected: false,
          id: question.bag.id,
          sourcesObj: this.getBagSourcesObj(question.bag.sources)
        }
      };
      return bagQuesData;
    },
    getBagSourcesObj(sources) {
      return sources.reduce((acc, source) => {
        acc[source.id] = {};
        source.questions.forEach(ques => {
          acc[source.id][ques.id] = this.getQuestionData(ques);
        });
        return acc;
      }, {});
    },
    mapQuestions(questions, parentsTree = []) {
      return questions.map(ques => {
        if (ques.bag !== null) {
          const formalizedSources = ques.bag.sources.map(source => ({
            ...source,
            questions: this.formalizeSourceQuestions(
              ques.id,
              ques.bag.id,
              source.id,
              parentsTree,
              source.questions
            )
          }));
          ques.bag.sources = [...formalizedSources];
        } else if (ques.choices && ques.choices.length) {
          // handle bag questions embedded within questions choices
          const mappedChoices = ques.choices.map(choice => {
            if (ques.answer && ques.answer.id && ques.answer.id === choice.id) {
              ques.answer = { ...choice }; // map selected choice data to answer
            }
            if (choice.questions) {
              choice.questions = this.mapQuestions(choice.questions);
            }
            return choice;
          });
          ques.choices = mappedChoices;
        }
        return ques;
      });
    },
    formalizeSourceQuestions(
      grandQuesId,
      bagId,
      sourceId,
      parentsTree,
      sourceQuestions
    ) {
      return sourceQuestions.map(subQues => {
        const quesParentsTree = {
          bagId,
          sourceId,
          grandQuesId,
          quesId: subQues.id
        };
        const quesData = {
          ...subQues,
          parentsTree: [...parentsTree, { ...quesParentsTree }] // for nested bags insha'Allah
        };
        if (subQues.answer && subQues.answer.questions) {
          subQues.answer.questions = this.mapQuestions(
            subQues.answer.questions,
            [...quesData.parentsTree]
          );
        }
        return quesData;
      });
    },
    questionsDisplay(id) {
      this.loadingData = true;
      this.globalServerErrors = null;
      ShowData({
        reqName: `phases`,
        id
      })
        .then(res => {
          this.phase = res.data.phase;
          this.phase.questions = this.mapQuestions(this.phase.questions);
          this.form = {};
          this.setFormData(this.phase.questions);
        })
        .catch(() => {})
        .finally(() => {
          this.loadingData = false;
        });
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    indexApplication() {
      IndexData({ reqName: "applications" })
        .then(res => {
          const { data } = res.data;
          this.application = data;
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {});
    },
    getBagQuesDataForReq(quesObj) {
      return Object.keys(quesObj.bagData.sourcesObj).reduce(
        (acc, sourceId) => {
          if (!quesObj.bagData.selected) {
            return acc;
          }

          const quesWithValArr = Object.keys(
            quesObj.bagData.sourcesObj[sourceId]
          )
            .filter(
              subQuesId =>
                quesObj.bagData.sourcesObj[sourceId][subQuesId] ||
                quesObj.bagData.sourcesObj[sourceId][subQuesId] === 0
            )
            .map(subQuesId => ({
              sourceId,
              quesId: subQuesId,
              value: quesObj.bagData.sourcesObj[sourceId][subQuesId]
            }));
          if (quesWithValArr.length) {
            acc.bagReqData.sources.push(sourceId);
            acc.subQuesReqData.push(...quesWithValArr);
          }
          return acc;
        },
        {
          bagReqData: { quesId: quesObj.id, sources: [] },
          subQuesReqData: []
        }
      );
    },
    formalizeReqData() {
      const formData = new FormData();
      let dataIndex = 0;
      Object.keys(this.form).forEach(quesId => {
        // case bag question
        if (this.form[quesId].id) {
          const { bagReqData, subQuesReqData } = this.getBagQuesDataForReq(
            this.form[quesId]
          );
          if (bagReqData.sources.length) {
            formData.append(
              `answers[${dataIndex}][question]`,
              bagReqData.quesId
            );
            formData.append(
              `answers[${dataIndex}][value][]`,
              bagReqData.sources
            );
            dataIndex += 1;

            subQuesReqData.forEach(subQues => {
              formData.append(
                `answers[${dataIndex}][source]`,
                subQues.sourceId
              );
              formData.append(
                `answers[${dataIndex}][question]`,
                subQues.quesId
              );
              formData.append(`answers[${dataIndex}][value]`, subQues.value);
              dataIndex += 1;
            });
          }
        } else if (Array.isArray(this.form[quesId])) {
          formData.append(`answers[${dataIndex}][question]`, quesId);
          this.form[quesId].forEach((questionId, arrIndex) => {
            if (this.form[quesId].length > 0) {
              formData.append(
                `answers[${dataIndex}][value][]`,
                this.form[quesId][arrIndex].id
              );
            } else {
              formData.append(`answers[${dataIndex}][value][]`, "");
            }
          });
          dataIndex += 1;
        } else {
          // sure about removing the above condition?
          formData.append(`answers[${dataIndex}][question]`, quesId);
          formData.append(`answers[${dataIndex}][value]`, this.form[quesId]);
          dataIndex += 1;
        }
      });
      formData.append("phase", this.$route.params.id);
      formData.append("_method", "put");
      return { reqData: formData, emptyBody: !dataIndex };
    },
    haveAnswers(questions) {
      return questions.some(ques =>
        !(typeof ques.answer === "object")
          ? ques.answer.id || ques.answer.length
          : ques.answer
      ); // find question that has no answer
    },
    validateRequired(questions) {
      return !questions.some(ques => {
        if (ques.bag !== null) {
          if (
            !this.form[ques.id] ||
            !this.form[ques.id].bagData ||
            !this.form[ques.id].bagData.selected
          ) {
            return !!ques.required;
          }
          return ques.bag.sources.every(
            source => !this.validateRequired(source.questions)
          );
        } else {
          return typeof ques.answer === "object" && ques.answer !== null
            ? ques.required &&
                !(ques.answer.id || ques.answer.length || ques.answer.type)
            : ques.required && !ques.answer;
        }
      });
    },
    handleSave(stepDirection) {
      const { reqData, emptyBody } = this.formalizeReqData();
      if (!this.validateRequired(this.phase.questions)) {
        this.globalServerErrors = [["Please fill all required fields"]];
        return;
      }

      if (
        emptyBody &&
        this.haveAnswers(this.phase.questions) &&
        this.phase.next == null
      ) {
        this.pushRouteByDirection(stepDirection);
      } else {
        this.updateUserApplication(reqData, stepDirection);
      }
    },
    updateUserApplication(reqData, stepDirection) {
      UpdateData({
        reqName: `applications`,
        data: reqData,
        id: this.application.id
      })
        .then(() => {
          this.pushRouteByDirection(stepDirection);
        })
        .catch(err => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
        });
    },
    pushRouteByDirection(stepDirection) {
      if (stepDirection === "proceed") {
        this.dialog = true;
      } else if (stepDirection === "next" && this.phase.next) {
        this.$router.push(`/phase/${this.phase.next}`);
      } else if (stepDirection === "prev" && this.phase.previous) {
        this.$router.push(`/phase/${this.phase.previous}`);
      }
    },
    closeDialog() {
      this.dialog = false;
    }
  },
  watch: {
    $route: {
      handler(route) {
        this.questionsDisplay(route.params.id);
      },
      immediate: true
    }
  }
};
</script>
